import { BooleanInput, NumberInput, coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    effect,
    inject,
    input,
    signal,
    viewChild,
} from '@angular/core';
import { LabelFormatter, LineGraphDrawer, LineStyle, ScaleType, TickFormatter } from './line-graph-drawer';

export const DEFAULT_LINE_COLORS = ['steelblue', '#ff9800', '#ff9800'];

export const DEFAULT_LINE_STYLES: LineStyle[] = ['solid', 'dashed', 'dashed'];

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'se-line-graph',
    templateUrl: './line-graph.component.html',
    styleUrls: ['./line-graph.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class LineGraphComponent implements OnDestroy, OnInit {
    private readonly hostElement = inject(ElementRef);

    graph = viewChild.required<ElementRef<SVGElement>>('graph');

    width = input<NumberInput>();
    height = input<NumberInput>();

    labelFormatter = input<LabelFormatter>((_, xValue, yValue, __, ___) => `${yValue}: ${xValue}`);
    labelXRotation = input<NumberInput>(0);
    labelYRotation = input<NumberInput>(0);

    marginX = input<NumberInput>(25);
    marginY = input<NumberInput>(25);

    paddingX = input<NumberInput>(0);
    paddingY = input<NumberInput>(0);

    lines = input.required<any[][] | null>();
    colors = input<string[] | null>(null);
    styles = input<LineStyle[] | null>(null);

    propX = input<string>('x');
    propY = input<string>('y');

    scaleX = input<ScaleType>('linear');
    scaleY = input<ScaleType>('linear');

    ticksX = input<NumberInput>(undefined);
    ticksY = input<NumberInput>(undefined);

    tickFormatterX = input<TickFormatter | undefined>(undefined);
    tickFormatterY = input<TickFormatter | undefined>(undefined);

    debug = input<BooleanInput>(false);

    completeRange = input<BooleanInput>(false);

    private drawer = new LineGraphDrawer();

    private resizeObserver!: ResizeObserver;
    private currentWidth = signal<number>(300);
    private currentHeight = signal<number>(300);
    private observeWidth = true;
    private observeHeight = true;

    constructor() {
        effect(() => {
            const currentWidth = this.currentWidth();
            const currentHeight = this.currentHeight();
            const lines = this.lines();

            if (currentWidth && currentHeight && lines) {
                this.onLinesChange();
            }
        });
    }

    ngOnInit() {
        this.observeWidth = this.width === undefined;
        this.observeHeight = this.height === undefined;
        this.resizeObserver = new ResizeObserver((entries) => this.onResize(entries));
        this.resizeObserver.observe(this.hostElement.nativeElement);

        const width = this.width();
        const height = this.height();

        this.currentWidth.set(width ? coerceNumberProperty(width) : 300);
        this.currentHeight.set(height ? coerceNumberProperty(height) : 300);
    }

    ngOnDestroy() {
        this.resizeObserver.disconnect();
    }

    private onLinesChange() {
        const lines = this.lines() ?? [];

        this.drawer.draw(this.graph().nativeElement, {
            colors: this.colors() ?? DEFAULT_LINE_COLORS,
            completeRange: coerceBooleanProperty(this.completeRange()),
            debug: coerceBooleanProperty(this.debug()),
            height: this.currentHeight(),
            labelFormatter: this.labelFormatter(),
            labelXRotation: coerceNumberProperty(this.labelXRotation()),
            labelYRotation: coerceNumberProperty(this.labelYRotation()),
            lines,
            lineStyles: this.styles() ?? DEFAULT_LINE_STYLES,
            marginX: coerceNumberProperty(this.marginX()),
            marginY: coerceNumberProperty(this.marginY()),
            paddingX: coerceNumberProperty(this.paddingX()),
            paddingY: coerceNumberProperty(this.paddingY()),
            propX: this.propX(),
            propY: this.propY(),
            scaleX: this.scaleX(),
            scaleY: this.scaleY(),
            ticksX: coerceNumberProperty(this.ticksX()),
            ticksY: coerceNumberProperty(this.ticksY()),
            tickFormatterX: this.tickFormatterX(),
            tickFormatterY: this.tickFormatterY(),
            width: this.currentWidth(),
        });
    }

    private onResize(entries: ResizeObserverEntry[]) {
        let targetWidth = undefined;
        let targetHeight = undefined;

        // Per ogni entry guarda le nuove dimensioni.
        for (const entry of entries) {
            if (entry.contentRect) {
                // Assegna le nuove larghezze / altezze solo se non sono impostate come fisse dall'esterno.
                targetWidth = this.observeWidth ? entry.contentRect.width : undefined;
                targetHeight = this.observeHeight ? entry.contentRect.height : undefined;
            }
        }

        if ((targetWidth && targetWidth < 250) || (targetHeight && targetHeight < 100)) {
            this.drawer.clear(this.graph().nativeElement);
            return;
        }

        if (targetWidth !== undefined || targetHeight !== undefined) {
            this.currentWidth.set(targetWidth ?? this.currentWidth());
            this.currentHeight.set(targetHeight ?? this.currentHeight());
            this.onLinesChange();
        }
    }
}
